/**
 * Languages that `CodeBlock` will syntax-highlight.
 *
 * The list is intentionally curated rather than open-ended: when a consumer
 * passes a `SupportedLanguage` we guarantee that the underlying syntax
 * highlighter has a grammar for it.
 *
 * To add a language: confirm the highlighter we use today supports it (see
 * the adapter that consumes this list, e.g., refractor-adapter.ts), add the key
 * here in alphabetical order with the existing entries, and add any necessary
 * translation in the adapter.
 *
 * Names are deliberately neutral and human-friendly (e.g. `dockerfile`, not
 * `docker`; `html`, not `markup`). We intentionally do not support all potential
 * aliases for a language. The consumer can handle aliasing and the adapter is
 * responsible for translating to whatever names the backing highlighter expects.
 */
export const SUPPORTED_LANGUAGES = [
	'abap',
	'abnf',
	'actionscript',
	'ada',
	'apacheconf',
	'apex',
	'applescript',
	'asciidoc',
	'autoit',
	'bash',
	'batch',
	'brainfuck',
	'c',
	'clojure',
	'cmake',
	'coffeescript',
	'cpp',
	'csharp',
	'css',
	'cypher',
	'd',
	'dart',
	'diff',
	'dockerfile',
	'elixir',
	'erlang',
	'excel',
	'fortran',
	'fsharp',
	'gherkin',
	'glsl',
	'go',
	'graphql',
	'groovy',
	'haml',
	'handlebars',
	'haskell',
	'haxe',
	'hcl',
	'html',
	'ini',
	'java',
	'javascript',
	'json',
	'julia',
	'kotlin',
	'latex',
	'less',
	'lisp',
	'livescript',
	'lua',
	'makefile',
	'markdown',
	'objectivec',
	'ocaml',
	'pascal',
	'perl',
	'php',
	'powershell',
	'prolog',
	'protobuf',
	'pug',
	'python',
	'qml',
	'r',
	'rego',
	'restructuredtext',
	'ruby',
	'rust',
	'sass',
	'scala',
	'scheme',
	'scss',
	'smalltalk',
	'sol',
	'sparql',
	'sql',
	'stylus',
	'swift',
	'tcl',
	'tsx',
	'twig',
	'typescript',
	'vb',
	'vbnet',
	'verilog',
	'vim',
	'xml',
	'yaml',
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const SUPPORTED_LANGUAGE_SET: Set<string> = new Set(SUPPORTED_LANGUAGES);

/**
 * Type guard that narrows an arbitrary string to `SupportedLanguage`.
 *
 * Useful for code that pulls language identifiers from external data (API
 * responses, filename mapping helpers, etc.) and needs to safely hand the
 * result to `<CodeBlock language={...} />`.
 */
export const isSupportedLanguage = (value: string): value is SupportedLanguage =>
	SUPPORTED_LANGUAGE_SET.has(value);
